import React, { useState, useRef, useEffect } from 'react';
import { View, Text, ScrollView, TextInput, Pressable, Platform, ActivityIndicator } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { Comment, CommentThread as CommentThreadType } from '../../shared/types/comments';
import { useCommentStore } from '../../store/commentStore';
import { ChatMessage } from './ChatMessage';
import { addColorOpacity } from '../../shared/utils/styles';
import { getShortDisplayName } from '../../shared/utils/user';
import { Ionicons } from '@expo/vector-icons';
import { logger } from '../../shared/utils/logger';

interface ChatThreadProps {
  thread: CommentThreadType;
  maxHeight?: number | string;
  onOpenFullPage?: () => void;
}

export function ChatThread({ thread, maxHeight, onOpenFullPage }: ChatThreadProps) {
  const styles = useStyles();
  const { isMobile, isSmallMobile } = useResponsive();
  
  const { 
    comments: allComments, 
    loadingComments,
    loadThread, 
    createComment 
  } = useCommentStore();

  // Get comments for this specific thread
  const threadComments = thread?.id ? allComments[thread.id] || [] : [];
  const loading = thread?.id ? loadingComments[thread.id] || false : false;

  const [newMessage, setNewMessage] = useState('');
  const [replyingTo, setReplyingTo] = useState<Comment | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollViewRef = useRef<ScrollView>(null);
  const hasLoadedRef = useRef(false);

  // Load thread comments on mount
  useEffect(() => {
    if (thread?.id && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadThread(thread.id);
    }
  }, [thread?.id, loadThread]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (threadComments.length > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [threadComments.length]);

  // Flatten nested comments into a single list for chat display
  const flattenComments = (commentList: Comment[]): Comment[] => {
    // Safety check - ensure commentList is an array
    if (!commentList || !Array.isArray(commentList)) {
      return [];
    }
    
    const result: Comment[] = [];
    
    const addComment = (comment: Comment) => {
      result.push(comment);
      if (comment.replies && Array.isArray(comment.replies) && comment.replies.length > 0) {
        comment.replies.forEach(reply => addComment(reply));
      }
    };
    
    commentList.forEach(comment => addComment(comment));
    
    // Sort by created_at ascending (oldest first, newest at bottom)
    return result.sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  };

  const flatComments = flattenComments(threadComments);

  // Group messages by date
  const groupByDate = (msgs: Comment[]) => {
    const groups: { date: string; label: string; messages: Comment[] }[] = [];
    
    msgs.forEach(msg => {
      const msgDate = new Date(msg.created_at);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      let label: string;
      const dateKey = msgDate.toDateString();
      
      if (msgDate.toDateString() === today.toDateString()) {
        label = 'Today';
      } else if (msgDate.toDateString() === yesterday.toDateString()) {
        label = 'Yesterday';
      } else {
        label = msgDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          month: 'short', 
          day: 'numeric' 
        });
      }
      
      const existingGroup = groups.find(g => g.date === dateKey);
      if (existingGroup) {
        existingGroup.messages.push(msg);
      } else {
        groups.push({ date: dateKey, label, messages: [msg] });
      }
    });
    
    return groups;
  };

  const messageGroups = groupByDate(flatComments);

  const handleSendMessage = async () => {
    const trimmed = newMessage.trim();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await createComment({
        thread_id: thread.id,
        content: trimmed,
        parent_comment_id: replyingTo?.id || null,
      });
      setNewMessage('');
      setReplyingTo(null);
      
      // Scroll to bottom after sending
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 200);
    } catch (error) {
      logger.error('Error sending message', error, 'chat');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReply = (comment: Comment) => {
    setReplyingTo(comment);
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Responsive sizing
  const inputFontSize = isSmallMobile ? 14 : 15;
  const spacing = isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md;

  return (
    <View style={{ flex: 1, minHeight: 0 }}>
      {/* Messages area */}
      <ScrollView
        ref={scrollViewRef}
        style={{
          flex: 1,
          maxHeight: maxHeight ? maxHeight as number : (isSmallMobile ? 450 : isMobile ? 500 : 550),
          minHeight: isMobile ? 300 : 350,
        }}
        contentContainerStyle={{
          paddingVertical: spacing,
          flexGrow: 1,
        }}
        showsVerticalScrollIndicator={true}
      >
        {loading && flatComments.length === 0 ? (
          <View style={{ 
            padding: styles.utils.spacing.xl, 
            alignItems: 'center' 
          }}>
            <ActivityIndicator size="small" color={styles.tokens.color.primary} />
            <Text style={{
              marginTop: styles.utils.spacing.sm,
              fontSize: 13,
              color: addColorOpacity(styles.tokens.color.onSurface, 0.6),
            }}>
              Loading messages...
            </Text>
          </View>
        ) : flatComments.length === 0 ? (
          <View style={{ 
            padding: styles.utils.spacing.xl, 
            alignItems: 'center' 
          }}>
            <Ionicons 
              name="chatbubbles-outline" 
              size={32} 
              color={addColorOpacity(styles.tokens.color.onSurface, 0.3)} 
            />
            <Text style={{
              marginTop: styles.utils.spacing.sm,
              fontSize: 14,
              color: addColorOpacity(styles.tokens.color.onSurface, 0.5),
              textAlign: 'center',
            }}>
              No messages yet.{'\n'}Start the conversation!
            </Text>
          </View>
        ) : (
          messageGroups.map((group) => (
            <View key={group.date}>
              {/* Date divider */}
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingHorizontal: spacing,
                paddingVertical: styles.utils.spacing.sm,
              }}>
                <View style={{
                  flex: 1,
                  height: 1,
                  backgroundColor: addColorOpacity(styles.tokens.color.onSurface, 0.1),
                }} />
                <Text style={{
                  paddingHorizontal: styles.utils.spacing.sm,
                  fontSize: 11,
                  color: addColorOpacity(styles.tokens.color.onSurface, 0.5),
                  fontWeight: '500',
                }}>
                  {group.label}
                </Text>
                <View style={{
                  flex: 1,
                  height: 1,
                  backgroundColor: addColorOpacity(styles.tokens.color.onSurface, 0.1),
                }} />
              </View>

              {/* Messages */}
              {group.messages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  comment={msg}
                  onReply={handleReply}
                  showActions={true}
                />
              ))}
            </View>
          ))
        )}
      </ScrollView>

      {/* Reply indicator */}
      {replyingTo && (
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: spacing,
          paddingVertical: styles.utils.spacing.xs,
          backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.08),
          borderTopWidth: 1,
          borderTopColor: addColorOpacity(styles.tokens.color.primary, 0.2),
        }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 }}>
            <Ionicons 
              name="return-up-back" 
              size={14} 
              color={styles.tokens.color.primary} 
            />
            <Text 
              style={{
                fontSize: 12,
                color: styles.tokens.color.primary,
              }}
              numberOfLines={1}
            >
              Replying to {getShortDisplayName(replyingTo.created_by_user)}
            </Text>
          </View>
          <Pressable onPress={cancelReply} hitSlop={8}>
            <Ionicons 
              name="close" 
              size={18} 
              color={addColorOpacity(styles.tokens.color.onSurface, 0.6)} 
            />
          </Pressable>
        </View>
      )}

      {/* Input area */}
      <View style={{
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: isSmallMobile ? 6 : 8,
        paddingHorizontal: spacing,
        paddingVertical: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm,
        borderTopWidth: 1,
        borderTopColor: styles.tokens.color.border,
        backgroundColor: styles.tokens.color.surface,
      }}>
        <TextInput
          value={newMessage}
          onChangeText={setNewMessage}
          placeholder="Write a message..."
          placeholderTextColor={addColorOpacity(styles.tokens.color.onSurface, 0.4)}
          multiline
          maxLength={2000}
          spellCheck={true}
          autoCorrect={true}
          autoCapitalize="sentences"
          style={{
            flex: 1,
            minHeight: 36,
            maxHeight: 100,
            backgroundColor: addColorOpacity(styles.tokens.color.onSurface, 0.05),
            borderRadius: 18,
            paddingHorizontal: 14,
            paddingVertical: 8,
            paddingRight: 14,
            fontSize: inputFontSize,
            color: styles.tokens.color.onSurface,
            ...(Platform.OS === 'web' && { 
              outlineWidth: 0,
              resize: 'none',
            }),
          }}
          onSubmitEditing={handleSendMessage}
          blurOnSubmit={false}
        />
        
        <Pressable
          onPress={handleSendMessage}
          disabled={!newMessage.trim() || isSubmitting}
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            backgroundColor: newMessage.trim() && !isSubmitting
              ? styles.tokens.color.primary
              : addColorOpacity(styles.tokens.color.onSurface, 0.1),
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isSubmitting ? (
            <ActivityIndicator size="small" color={styles.tokens.color.onPrimary} />
          ) : (
            <Ionicons 
              name="send" 
              size={18} 
              color={newMessage.trim() 
                ? styles.tokens.color.onPrimary 
                : addColorOpacity(styles.tokens.color.onSurface, 0.3)
              } 
            />
          )}
        </Pressable>
      </View>

      {/* Open Full Page link */}
      {onOpenFullPage && (
        <Pressable
          onPress={onOpenFullPage}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            paddingVertical: styles.utils.spacing.sm,
            borderTopWidth: 1,
            borderTopColor: styles.tokens.color.border,
          }}
        >
          <Ionicons 
            name="open-outline" 
            size={14} 
            color={styles.tokens.color.primary} 
          />
          <Text style={{
            fontSize: 13,
            color: styles.tokens.color.primary,
            fontWeight: '500',
          }}>
            Open Full Page
          </Text>
        </Pressable>
      )}
    </View>
  );
}

